import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Table } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { Link, Loader, Pagination } from 'components';
import { IRootState } from 'config/redux';
import { RouteConstants, ServiceConstants } from 'constant';
import { useListHook } from 'hooks';
import { ProfileService } from 'services';
import { tableClasses } from 'styles';
import { ProfileExtendedResponse } from 'types';
import { AppUtils } from 'utils';

const { REQUEST_PENDING, REQUEST_PROFILES_LIST_ID } = ServiceConstants;

const TemplateListTab = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const serviceObject = useSelector((state: IRootState) => state.service.object[REQUEST_PROFILES_LIST_ID]);
  const { content, page, totalPages, setPage, updatePageable, resetList } = useListHook();

  // Initialize page to 0 on mount
  useEffect(() => {
    resetList();
  }, [resetList]);

  // Fetch data when page changes
  useEffect(() => {
    if (page === undefined || page === -1) return;
    ProfileService.profilesAll({ page, search: '', pageSize: 20 });
  }, [page]);

  useEffect(() => {
    AppUtils.resolveServiceObject(serviceObject, REQUEST_PROFILES_LIST_ID, updatePageable);
  }, [serviceObject, updatePageable]);

  const isLoading = useCallback(() => serviceObject && serviceObject.status === REQUEST_PENDING, [serviceObject]);

  const handleRowClick = useCallback((templateId: number) => {
    navigate(`${RouteConstants.ROUTE_PERMISSIONS}/sabloni/${templateId}`);
  }, [navigate]);

  const rows = useMemo(() => {
    if (!content) return null;
    return content.map((template: ProfileExtendedResponse, index: number) => {
      const rowBg = index % 2 === 1 ? 'var(--mantine-color-gray-1)' : 'var(--mantine-color-gray-0)';
      return (
        <Table.Tr
          key={template.id}
          onClick={() => handleRowClick(template.id)}
          style={{ cursor: 'pointer' }}
        >
          <Table.Td style={{ backgroundColor: rowBg }}>
            <Link
              to={`${RouteConstants.ROUTE_PERMISSIONS}/sabloni/${template.id}`}
              onClick={(e) => e.stopPropagation()}
            >
              {template.name}
            </Link>
          </Table.Td>
          <Table.Td style={{ backgroundColor: rowBg }}>{template.numberOfUsers}</Table.Td>
        </Table.Tr>
      );
    });
  }, [content, handleRowClick]);

  return (
    <Box pos="relative">
      <Loader overlay={true} visible={content === undefined || isLoading()} />
      <Table className={tableClasses.table}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={{ width: '15%' }}>{t('permissions.templates.name')}</Table.Th>
            <Table.Th>{t('permissions.templates.agentCount')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>{rows}</Table.Tbody>
      </Table>
      <Pagination value={page} onChange={setPage} total={totalPages} />
    </Box>
  );
};

export default TemplateListTab;
